import { useState } from 'react'
import { requestPasswordReset as apiRequestReset, resetPassword as apiResetPassword } from '../services/authService.js'

/**
 * Custom hook for password reset functionality
 * @returns {Object} - Password reset state and functions
 */
export const usePasswordReset = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)

  /**
   * Request password reset
   * @param {string} email - User email
   * @returns {Promise<boolean>} - Success status
   */
  const requestReset = async (email) => {
    setIsLoading(true)
    setError(null)
    setSuccess(false)

    try {
      const result = await apiRequestReset(email)
      
      if (result.success) {
        setSuccess(true)
        setError(null)
        return true
      } else {
        setError(result.error)
        setSuccess(false)
        return false
      }
    } catch (err) {
      const errorMessage = err.message || 'Erreur lors de la demande de réinitialisation'
      setError(errorMessage)
      setSuccess(false)
      return false
    } finally {
      setIsLoading(false)
    }
  }

  /**
   * Reset password with token
   * @param {string} token - Reset token
   * @param {string} newPassword - New password
   * @returns {Promise<boolean>} - Success status
   */
  const resetPassword = async (token, newPassword) => {
    setIsLoading(true)
    setError(null)
    setSuccess(false)

    try {
      const result = await apiResetPassword(token, newPassword)
      
      if (result.success) {
        setSuccess(true)
        setError(null)
        return true
      } else {
        setError(result.error)
        setSuccess(false)
        return false
      }
    } catch (err) {
      const errorMessage = err.message || 'Erreur lors de la réinitialisation du mot de passe'
      setError(errorMessage)
      setSuccess(false)
      return false
    } finally {
      setIsLoading(false)
    }
  }

  /**
   * Reset hook state
   */
  const reset = () => {
    setIsLoading(false)
    setError(null)
    setSuccess(false)
  }

  return {
    requestReset,
    resetPassword,
    isLoading,
    error,
    success,
    reset
  }
}

export default usePasswordReset

